import { useState } from "react"
import { X } from "lucide-react"
import { Button } from "@/components/ui/Button"
import { DatePicker } from "@/components/ui/DatePicker"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/Select"
import { ACTION_TYPES, type ActionType, CHANNELS, type PushAction } from "../data"
import MentionInput from "./MentionInput"
import PicPicker from "./PicPicker"

// Wireframe-only ids. crypto.randomUUID needs HTTPS, and atlas.test is plain HTTP.
let nextId = 0

interface Props {
  action: PushAction | null
  giftable: boolean
  onSave: (action: PushAction) => void
  // Left out for the always-open blank row, which has nothing to cancel.
  onCancel?: () => void
}

// The options on one line (type, channel, dates, PIC), the note underneath
// with room for several lines, Save at the bottom right. Everything except the
// type is optional. A one-day action has the same From and To.
export default function ActionEditor({ action, giftable, onSave, onCancel }: Props) {
  const [type, setType] = useState<ActionType | "">(action?.type ?? "")
  const [note, setNote] = useState(action?.note ?? "")
  const [channel, setChannel] = useState<string>(action?.channel ?? "")
  const [from, setFrom] = useState<string | null>(action?.from ?? null)
  const [to, setTo] = useState<string | null>(action?.to ?? null)
  const [pics, setPics] = useState<string[]>(action?.pics ?? [])

  const save = () => {
    if (!type) return
    onSave({ id: action?.id ?? `new-${++nextId}`, type, note: note.trim(), channel: channel || null, from, to: to ?? from, pics })
  }

  return (
    <div className="space-y-2 bg-muted/50 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={type} onValueChange={(v) => setType(v as ActionType)}>
          <SelectTrigger className="w-52 bg-background font-medium">
            <SelectValue placeholder="Pick a type" />
          </SelectTrigger>
          <SelectContent>
            {ACTION_TYPES.map((t) => (
              <SelectItem
                key={t}
                value={t}
                disabled={t === "Gifting" && !giftable}
                suffix={t === "Gifting" && !giftable ? <span className="ml-2 text-xs">(can't be gifted)</span> : null}
              >
                {t}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={channel} onValueChange={setChannel}>
          <SelectTrigger className="w-36 bg-background">
            <SelectValue placeholder="Channel" />
          </SelectTrigger>
          <SelectContent>
            {CHANNELS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1">
          <DatePicker
            value={from ?? undefined}
            onChange={(v) => { setFrom(v); if (to && to < v) setTo(v) }}
            placeholder="From"
            className="w-36"
          />
          <span>–</span>
          <DatePicker value={to ?? undefined} onChange={setTo} placeholder="To" className="w-36" fromDate={from ?? undefined} />
          {(from || to) && (
            <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => { setFrom(null); setTo(null) }} aria-label="Clear dates">
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
        <PicPicker value={pics} onChange={setPics} />
      </div>
      <MentionInput value={note} onChange={setNote} onSubmit={save} placeholder="Note. Type # to link a product" />
      <div className="flex items-center justify-end gap-2">
        {onCancel && <Button size="sm" variant="ghost" onClick={onCancel}>Cancel</Button>}
        <Button size="sm" onClick={save} disabled={!type}>Save</Button>
      </div>
    </div>
  )
}
